import { useEffect, useRef, useState } from 'react';

/**
 * A tiny SWR-shaped fetcher. We deliberately do not pull in `swr` for this
 * MVP (no new dependencies). For our needs — load once on mount, expose
 * `{ data, error, loading }`, allow manual `mutate`/`refetch`, dedupe via the
 * `key` string — this is enough.
 *
 * The fetcher is called with no args; capture closure state on the caller
 * side. Render-time dependency on `key` keys the request.
 */
export interface FetcherResult<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  refetch: () => void;
  /** Imperatively set the result. Useful for optimistic updates. */
  mutate: (next: T | undefined) => void;
}

export function useFetcher<T>(
  key: string | undefined,
  fetcher: () => Promise<T>,
): FetcherResult<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!key) {
      setData(undefined);
      setError(undefined);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setError(undefined);
    setLoading(true);
    fetcherRef
      .current()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The fetcher is captured in a ref and considered "frozen" per key —
    // re-running on function-identity changes leads to cascading refetches
    // with inline arrow functions (the common caller pattern). Only `key`
    // and `tick` (manual refetch) re-trigger the effect.
  }, [key, tick]);

  return {
    data,
    error,
    loading,
    refetch: () => setTick((t) => t + 1),
    mutate: setData,
  };
}
